import {
  ARENA_SHAPES,
  GAME_MODES,
  MAP_IDS,
  MAP_MODES,
  MAP_PROPS_MODES,
  OBSTACLE_DENSITIES,
  type MatchSettings,
  type ObstacleDensity,
} from '../types';
import { isMapId, MAP_PRESETS } from '../data/maps';
import { isFrictionPreset } from '../data/friction';
import { hostArenaOf } from './maps';
import { clamp } from '../physics/vec2';

export const DEFAULT_SETTINGS: Readonly<MatchSettings> = {
  mode: 'ffa',
  rounds: 10,
  startingGold: 20,
  goldPerRound: 10,
  shopSeconds: 30,
  ...hostArenaOf(MAP_PRESETS.emberRing.arena),
  frictionPreset: 'normal',
  knockbackMult: 1,
  mapMode: 'rotate',
  mapPool: [...MAP_IDS],
  startMapId: 'emberRing',
  mapProps: 'full',
  racialAbilities: true,
  obstacleDensity: 'normal',
};

/** A synced density: `''` (never written, older state) or anything unknown reads as `normal`. */
export function obstacleDensityOf(value: unknown): ObstacleDensity {
  return OBSTACLE_DENSITIES.find((d) => d === value) ?? 'normal';
}

type NumericSetting = {
  [K in keyof MatchSettings]: MatchSettings[K] extends number ? K : never;
}[keyof MatchSettings];

/** Host-adjustable ranges; `int` settings are rounded. */
export const SETTINGS_LIMITS: Record<NumericSetting, { min: number; max: number; int: boolean }> = {
  rounds: { min: 1, max: 30, int: true },
  startingGold: { min: 0, max: 500, int: true },
  goldPerRound: { min: 0, max: 100, int: true },
  shopSeconds: { min: 5, max: 120, int: true },
  startRadius: { min: 400, max: 1600, int: true },
  graceMs: { min: 0, max: 60_000, int: true },
  shrinkIntervalMs: { min: 3_000, max: 60_000, int: true },
  shrinkPct: { min: 0, max: 0.2, int: false },
  lavaDpsPct: { min: 0, max: 0.5, int: false },
  knockbackMult: { min: 0.25, max: 3, int: false },
};

/**
 * Merge a (possibly hostile) partial update over `base`, clamping every value into range.
 *
 * Map rules: an empty or all-invalid `mapPool` keeps the previous pool; a `startMapId` outside the
 * pool snaps to the pool's first map. In `fixed` mode, picking a map (or switching to `fixed`)
 * first copies that map's preset into the arena fields, then explicit arena fields in the same
 * patch are applied on top. The friction preset is independent of map and mode: it only
 * changes on a valid preset id.
 */
export function clampSettings(
  patch: Partial<MatchSettings>,
  base: Readonly<MatchSettings> = DEFAULT_SETTINGS,
): MatchSettings {
  const out: MatchSettings = { ...base, mapPool: [...base.mapPool] };

  if (patch.mode !== undefined && GAME_MODES.includes(patch.mode)) out.mode = patch.mode;
  if (isFrictionPreset(patch.frictionPreset)) out.frictionPreset = patch.frictionPreset;
  if (patch.mapMode !== undefined && MAP_MODES.includes(patch.mapMode)) {
    out.mapMode = patch.mapMode;
  }
  if (Array.isArray(patch.mapPool)) {
    const pool = MAP_IDS.filter((id) => patch.mapPool!.includes(id));
    if (pool.length > 0) out.mapPool = pool;
  }
  if (patch.mapProps !== undefined && MAP_PROPS_MODES.includes(patch.mapProps)) {
    out.mapProps = patch.mapProps;
  }
  if (typeof patch.racialAbilities === 'boolean') out.racialAbilities = patch.racialAbilities;
  if (patch.obstacleDensity !== undefined && OBSTACLE_DENSITIES.includes(patch.obstacleDensity)) {
    out.obstacleDensity = patch.obstacleDensity;
  }
  if (isMapId(patch.startMapId)) out.startMapId = patch.startMapId;
  if (!out.mapPool.includes(out.startMapId)) out.startMapId = out.mapPool[0]!;

  const pickedMap = out.startMapId !== base.startMapId || isMapId(patch.startMapId);
  const switchedToFixed = base.mapMode !== 'fixed';
  if (out.mapMode === 'fixed' && (pickedMap || switchedToFixed)) {
    Object.assign(out, hostArenaOf(MAP_PRESETS[out.startMapId].arena));
  }

  for (const key of Object.keys(SETTINGS_LIMITS) as NumericSetting[]) {
    const value = patch[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    const { min, max, int } = SETTINGS_LIMITS[key];
    out[key] = clamp(int ? Math.round(value) : value, min, max);
  }
  if (patch.arenaShape !== undefined && ARENA_SHAPES.includes(patch.arenaShape)) {
    out.arenaShape = patch.arenaShape;
  }
  return out;
}
